/* eslint-disable react/jsx-props-no-spreading */
import {
  Typography, useMediaQuery, Theme, useTheme, SxProps,
} from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, { useMemo } from 'react';
import TabPanel from './TabPanel';

const tabsSx = (theme: Theme) => ({
  // Container for all tabs
  width: '100%',
  left: 0,
  // Media Queries - Becomes a bottom Navigation on mobile
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    bottom: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    height: theme.spacing(10),
    backgroundColor: theme.palette.background.paper,
  },
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    borderTop: 'revert',
    height: theme.spacing(2),
    '& .MuiTabs-root': {
      minHeight: theme.spacing(4),
    },
  },
});

const flexSx = (theme: Theme) => ({
  '& .MuiTabs-flexContainer': {
    [theme.breakpoints.down('md')]: {
      justifyContent: 'space-evenly',
    },
    [theme.breakpoints.up('md')]: {
      justifyContent: 'normal',
    },
    [theme.breakpoints.up('md')]: {
      '& .MuiTab-root': {
        paddingTop: theme.spacing(0),
        minHeight: theme.spacing(4),
      },
    },
  },
});

const tabContainerSx = (theme: Theme) => ({
  minHeight: theme.spacing(4),
  '& .MuiTabs-indicator': {
    borderRadius: theme.shape.borderRadius,
    height: 3,
    // Media Queries - Hide the indicator on Mobile
    [theme.breakpoints.down('md')]: {
      visibility: 'hidden',
    },
    [theme.breakpoints.up('md')]: {
      visibility: 'visible',
    },
  },
  [theme.breakpoints.down('lg')]: {
    zIndex: 10,
  },
});

const tabSx = (theme: Theme) => ({
  // Individual Tab Container
  minWidth: theme.spacing(8),
  height: theme.spacing(10),
  paddingTop: theme.spacing(1),
  boxSizing: 'border-box',
  // Font theme for Tabs
  ...theme.typography.subtitle1,
  textTransform: 'capitalize',
  [theme.breakpoints.up('md')]: {
    height: theme.spacing(2),
  },
});

interface PageTabItem {
  component: React.ReactNode;
  disabled?: boolean;
  icon: React.ReactElement;
  priority?: number;
  title: string;
}

export interface PageTabsProps {
  customTabSx?: SxProps;
  customTabPanelSx?: SxProps;
  tabs: {
    [key: string]: PageTabItem;
  };
  selectedTab: string;
  // eslint-disable-next-line no-unused-vars
  handleTabChange: (tab: string) => void;
}

const PageTabs: React.FC<React.PropsWithChildren<PageTabsProps>> = (props) => {
  const {
    handleTabChange, tabs, selectedTab, customTabSx, customTabPanelSx,
  } = props;
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down('md'));

  const sortedPageTabItems = useMemo(
    () => Object.entries(tabs)?.sort(
      (a, b) => (a[1].priority ?? 0) - (b[1].priority ?? 0),
    ),
    [tabs],
  );

  const _handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
  ) => {
    handleTabChange(value);
  };

  return (
    <>
      <Tabs
        value={selectedTab}
        sx={[tabsSx, flexSx, tabContainerSx]}
        indicatorColor="primary"
        textColor="primary"
        onChange={(event, value) => _handleChange(event, value)}
        aria-label="Page Tabs"
      >
        {/* Top Page Tabs */}
        {sortedPageTabItems
          && sortedPageTabItems.map((tabItem) => (
            <Tab
              data-testid="pageTab"
              disabled={tabItem[1].disabled ? tabItem[1].disabled : false}
              value={tabItem[0]}
              aria-selected={tabItem[0] === selectedTab}
              key={`tab-${tabItem[1].title}`}
              icon={isMobileSize ? tabItem[1].icon : ''}
              sx={[
                tabSx,
                ...(Array.isArray(customTabSx) ? customTabSx : [customTabSx]),
              ]}
              label={
                <Typography variant="subtitle1">{tabItem[1].title}</Typography>
              }
            />
          ))}
      </Tabs>
      {/* Only Render Tab Content if the current tab is selected - other tabs are not rendered */}

      {sortedPageTabItems
        && sortedPageTabItems.map((tabItem) => (tabItem[0] === selectedTab ? (
          <TabPanel
            customTabPanelSx={customTabPanelSx}
            key={`tabpanel-${tabItem[1].title}`}
            data-testid={tabItem[1].title}
            ariaLabel={tabItem[1].title}
            value={tabItem[0]}
            currentTab={selectedTab}
          >
            {tabItem[1].component}
          </TabPanel>
        ) : null))}
    </>
  );
};
export default PageTabs;
